import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import AuthWrapper from './components/AuthWrapper';
import LayoutContent from './components/LayoutContent';
import ClientRouterConfig from './components/ClientRouterConfig';
import ClientErrorBoundary from './components/ClientErrorBoundary';
import GlobalErrorHandler from './components/GlobalErrorHandler';
import AppBanners from './components/banners/AppBanners';
import Home from './pages/Home';
import Login from './pages/Login';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import DocumentEdit from './pages/DocumentEdit';
import DocumentPdf from './pages/DocumentPdf';
import NoteDetail from './pages/NoteDetail';
import NoteEdit from './pages/NoteEdit';
import OfflinePage from './pages/OfflinePage';
import OfflineExpired from './pages/OfflineExpired';
import SelectOrganization from './pages/SelectOrganization';
import AuthCallback from './pages/AuthCallback';
import NotFound from './pages/NotFound';
import Settings from './pages/Settings';
import { useCurrentPerson } from './hooks/useCurrentPerson';
import { LockProvider } from './contexts/LockContext';
import { setupGlobalErrorHandlers } from './lib/error-handler';
import { getSessionTracker } from './lib/session-tracker';
import { trackEvent } from './db/services/analytics_service';
import { updateLastOnlineTime, initializeLastOnlineTime } from './db/services/offline_time_tracker';
import { AppEvents } from './db/events';
import { requestPersistentStorage } from './lib/storage-persistence';

// Wrap Routes with Sentry for React Router v7 transaction naming
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

function AppContent() {
  const { currentPerson } = useCurrentPerson();

  // Initialize session tracker when user is logged in
  useEffect(() => {
    if (!currentPerson) return;

    initializeLastOnlineTime().catch((error) => {
      console.error('[App] Failed to initialize last online time:', error);
    });

    const sessionTracker = getSessionTracker();
    sessionTracker.resumeSession().catch((error) => {
      console.error('[App] Failed to start session tracking:', error);
    });

    return () => {
      sessionTracker.endSession().catch((error) => {
        console.error('[App] Failed to end session:', error);
      });
    };
  }, [currentPerson]);

  // A signed-in session is available (startup, or the person just became
  // readable after an unlock). Ask the data layer for a full refresh so a
  // reload on a non-dashboard route still picks up server changes; it is
  // debounced against the dashboard's own refresh. Keyed on the person id,
  // not the object: every refresh rewrites current_person (new identity,
  // same person) and must not trigger another refresh.
  const currentPersonId = currentPerson?.id ?? null;
  useEffect(() => {
    if (currentPersonId === null) return;
    window.dispatchEvent(new Event(AppEvents.SESSION_READY));
    // The device is about to hold this person's data: ask the browser not to
    // evict it. A no-op once granted.
    void requestPersistentStorage();
  }, [currentPersonId]);

  // Handle network status changes to refresh data when coming back online
  useEffect(() => {
    const handleOnline = () => {
      // Update last online time
      updateLastOnlineTime().catch((error) => {
        console.error('[App] Failed to update last online time:', error);
      });

      // Track coming online
      trackEvent('app.came_online').catch((error) => {
        console.error('[App] Failed to track app.came_online event:', error);
      });

      // Since we're now using useLiveQuery, data will automatically update
      // when the database changes. No manual refresh needed.
      if (currentPerson) {
        console.log('App came back online - data will automatically refresh via useLiveQuery');
      }
    };

    const handleOffline = () => {
      // Track going offline
      trackEvent('app.went_offline').catch((error) => {
        console.error('[App] Failed to track app.went_offline event:', error);
      });

      console.log('App went offline - changes will be queued for sync');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [currentPerson]);

  return (
    <AuthWrapper>
      <ClientErrorBoundary>
        <GlobalErrorHandler />
        <ClientRouterConfig />
        <LayoutContent>
          <SentryRoutes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Home />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/select-organization" element={<SelectOrganization />} />
            { /* private */}
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/clients/:id/notes/new" element={<NoteEdit />} />
            <Route path="/clients/:id/notes/:noteId" element={<NoteDetail />} />
            <Route path="/clients/:id/notes/:noteId/edit" element={<NoteEdit />} />
            <Route path="/clients/:id/documents/:documentId/edit" element={<DocumentEdit />} />
            <Route path="/clients/:id/documents/drafts/:localId/edit" element={<DocumentEdit />} />
            <Route path="/clients/:id/documents/:documentId/pdf" element={<DocumentPdf />} />
            <Route path="/clients/:id/documents/drafts/:localId/pdf" element={<DocumentPdf />} />
            <Route path="/settings" element={<Settings />} />
            { /* private end */}
            <Route path="/offline" element={<OfflinePage />} />
            <Route path="/offline-expired" element={<OfflineExpired />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </SentryRoutes>
        </LayoutContent>
      </ClientErrorBoundary>
    </AuthWrapper>
  );
}

function App() {
  // Setup global error handlers on mount
  useEffect(() => {
    setupGlobalErrorHandlers();
  }, []);

  // Detect PWA installation
  useEffect(() => {
    const handleAppInstalled = () => {
      console.log('[App] PWA installed successfully');
      trackEvent('app.installed').catch((error) => {
        console.error('[App] Failed to track app.installed event:', error);
      });
      // Browsers that refused persistent storage to a tab usually grant it to
      // an installed app, so ask again now.
      void requestPersistentStorage();
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  return (
    <Router>
      <LockProvider>
        <div className="fixed inset-0 flex flex-col">
          <AppBanners />
          <div className="relative flex-1 min-h-0">
            <AppContent />
          </div>
        </div>
      </LockProvider>
    </Router>
  );
}

export default App;